import React, { useState, useEffect } from 'react';
import { companyAPI } from '../../services/api';
import { useBrand } from '../../context/BrandContext';
import toast from 'react-hot-toast';
import { Palette, Save, RotateCcw, Eye } from 'lucide-react';

const DEFAULTS = {
  brand_primary:    '#0F2747',
  brand_accent:     '#2563EB',
  brand_success:    '#16A34A',
  brand_warning:    '#D97706',
  brand_danger:     '#DC2626',
  invoice_color:     '',
  estimate_color:    '',
  sales_order_color: '',
  purchase_order_color: '',
  sales_invoice_color:  '',
  purchase_invoice_color: '',
  invoice_border:       'none',
  estimate_border:      'none',
  sales_order_border:   'none',
  purchase_order_border:'none',
  purchase_invoice_border: 'none',
};

const PRESETS = [
  { name: 'Deep Navy + Blue',     primary: '#0F2747', accent: '#2563EB' },
  { name: 'Forest + Teal',        primary: '#064E3B', accent: '#0D9488' },
  { name: 'Royal + Indigo',       primary: '#312E81', accent: '#4F46E5' },
  { name: 'Charcoal + Rose',      primary: '#1F2937', accent: '#E11D48' },
  { name: 'Midnight + Violet',    primary: '#1E1B4B', accent: '#7C3AED' },
  { name: 'Slate + Orange',       primary: '#1E293B', accent: '#EA580C' },
];

const BORDER_STYLES = [
  { value: 'none',   label: 'None',     css: 'none' },
  { value: 'solid',  label: 'Solid',    css: '2px solid currentColor' },
  { value: 'dashed', label: 'Dashed',   css: '2px dashed currentColor' },
  { value: 'double', label: 'Double',   css: '3px double currentColor' },
  { value: 'thick',  label: 'Thick',    css: '3px solid currentColor' },
  { value: 'dotted', label: 'Dotted',   css: '2px dotted currentColor' },
];

const DOC_PREVIEWS = {
  invoice:          { icon: '📄', title: 'INVOICE', meta: 'INV-0001', labels: ['Bill To', 'Date', 'Due Date'] },
  estimate:         { icon: '📋', title: 'ESTIMATE', meta: 'EST-0001', labels: ['Customer', 'Date', 'Valid Until'] },
  sales_order:      { icon: '🛒', title: 'SALES ORDER', meta: 'SO-0001', labels: ['Customer', 'Date', 'Expected'] },
  purchase_order:   { icon: '📦', title: 'PURCHASE ORDER', meta: 'PO-0001', labels: ['Vendor', 'Date', 'Expected'] },
  purchase_invoice: { icon: '🧾', title: 'BILL', meta: 'BILL-0001', labels: ['Vendor', 'Date', 'Due Date'] },
};

function ColorField({ label, value, onChange, description }) {
  return (
    <div className="form-group" style={{ marginBottom: 12 }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input type="color" value={value || '#2563EB'} onChange={e => onChange(e.target.value)}
          style={{ width: 32, height: 28, border: '1px solid var(--border)', borderRadius: 4, cursor: 'pointer', padding: 0 }} />
        <span>{label}</span>
      </label>
      {description && <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>{description}</div>}
    </div>
  );
}

function DocColorField({ label, value, onChange, description, docType, accentFallback }) {
  const effectiveColor = value || accentFallback;
  const preview = DOC_PREVIEWS[docType] || DOC_PREVIEWS.invoice;
  return (
    <div className="form-group" style={{ marginBottom: 16 }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <input type="color" value={value || accentFallback || '#2563EB'} onChange={e => onChange(e.target.value)}
          style={{ width: 32, height: 28, border: '1px solid var(--border)', borderRadius: 4, cursor: 'pointer', padding: 0 }} />
        <span style={{ fontWeight: 600 }}>{label}</span>
        {!value && <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 400 }}>(using accent)</span>}
      </label>
      {description && <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 6 }}>{description}</div>}

      {/* Live mini-preview of the document */}
      <div style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden', background: '#fff' }}>
        {/* Header: letterhead placeholder + title */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', padding: '8px 10px 6px', borderBottom: `2px solid ${effectiveColor}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 22, height: 16, borderRadius: 3, background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, color: '#94a3b8' }}>Co.</div>
            <div>
              <div style={{ fontSize: 8, fontWeight: 700, color: '#334155', lineHeight: 1 }}>Acme Corp</div>
              <div style={{ fontSize: 6, color: '#94a3b8' }}>555-0101</div>
            </div>
          </div>
          <div style={{ fontSize: 11, fontWeight: 800, color: effectiveColor, letterSpacing: 0.3 }}>{preview.title}</div>
        </div>

        {/* Meta row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 10px', fontSize: 7 }}>
          <div>
            <span style={{ color: '#94a3b8' }}>{preview.labels[0]}</span>
            <div style={{ fontWeight: 600, color: '#334155' }}>Acme Corp</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div><span style={{ color: '#94a3b8' }}>#</span> <span style={{ fontWeight: 600, color: '#334155' }}>{preview.meta}</span></div>
            <div><span style={{ color: '#94a3b8' }}>Date</span> <span style={{ fontWeight: 600, color: '#334155' }}>01/15/2026</span></div>
          </div>
        </div>

        {/* Mini table header */}
        <div style={{ display: 'flex', background: '#f8fafc', padding: '3px 10px', fontSize: 6, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.3 }}>
          <span style={{ flex: 2 }}>Description</span>
          <span style={{ flex: 1, textAlign: 'right' }}>Qty</span>
          <span style={{ flex: 1, textAlign: 'right' }}>Amount</span>
        </div>
        <div style={{ display: 'flex', padding: '3px 10px', fontSize: 7, color: '#334155' }}>
          <span style={{ flex: 2 }}>Service item</span>
          <span style={{ flex: 1, textAlign: 'right' }}>1</span>
          <span style={{ flex: 1, textAlign: 'right', fontWeight: 600 }}>$500.00</span>
        </div>

        {/* Totals */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '4px 10px', borderTop: '1px solid #e2e8f0', gap: 20, fontSize: 7 }}>
          <div><span style={{ color: '#64748b' }}>Total</span> <span style={{ fontWeight: 700, color: effectiveColor }}>$500.00</span></div>
        </div>
      </div>
    </div>
  );
}

function BorderStyleField({ label, value, onChange, accentColor }) {
  const preview = BORDER_STYLES.find(b => b.value === value) || BORDER_STYLES[0];
  return (
    <div className="form-group" style={{ marginBottom: 12 }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <select value={value || 'none'} onChange={e => onChange(e.target.value)}
          style={{ width: 100, padding: '4px 6px', fontSize: 11, border: '1px solid var(--border)', borderRadius: 4, background: '#fff' }}>
          {BORDER_STYLES.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
        </select>
        <span>{label}</span>
      </label>
      {/* Visual preview */}
      <div style={{ marginTop: 4, padding: '6px 10px', background: '#f8fafc', borderRadius: 4, border: '1px solid var(--border)' }}>
        <div style={{ width: '100%', height: 0, borderTop: preview.css.replace('currentColor', accentColor || '#2563EB') }} />
      </div>
    </div>
  );
}

export default function ThemeBrandingSettings() {
  const [form, setForm] = useState({ ...DEFAULTS });
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const brand = useBrand();

  useEffect(() => {
    companyAPI.current()
      .then(c => {
        setCompany(c);
        setForm({
          brand_primary:           c.brand_primary           || DEFAULTS.brand_primary,
          brand_accent:            c.brand_accent            || DEFAULTS.brand_accent,
          brand_success:           c.brand_success           || DEFAULTS.brand_success,
          brand_warning:           c.brand_warning           || DEFAULTS.brand_warning,
          brand_danger:            c.brand_danger            || DEFAULTS.brand_danger,
          invoice_color:           c.invoice_color           || '',
          estimate_color:          c.estimate_color          || '',
          sales_order_color:       c.sales_order_color       || '',
          purchase_order_color:    c.purchase_order_color    || '',
          sales_invoice_color:     c.sales_invoice_color     || '',
          purchase_invoice_color:  c.purchase_invoice_color  || '',
          invoice_border:          c.invoice_border          || 'none',
          estimate_border:         c.estimate_border         || 'none',
          sales_order_border:      c.sales_order_border      || 'none',
          purchase_order_border:   c.purchase_order_border   || 'none',
          purchase_invoice_border: c.purchase_invoice_border || 'none',
        });
      })
      .catch(e => toast.error(e.message))
      .finally(() => setLoading(false));
  }, []);

  const set = (field) => (val) => setForm(f => ({ ...f, [field]: val }));

  const applyPreview = () => {
    brand?.apply({
      primary: form.brand_primary,
      accent: form.brand_accent,
      success: form.brand_success,
      warning: form.brand_warning,
      danger: form.brand_danger,
      invoice_color: form.invoice_color || null,
      estimate_color: form.estimate_color || null,
      sales_order_color: form.sales_order_color || null,
      purchase_order_color: form.purchase_order_color || null,
      sales_invoice_color: form.sales_invoice_color || null,
      purchase_invoice_color: form.purchase_invoice_color || null,
      invoice_border: form.invoice_border || 'none',
      estimate_border: form.estimate_border || 'none',
      sales_order_border: form.sales_order_border || 'none',
      purchase_order_border: form.purchase_order_border || 'none',
      purchase_invoice_border: form.purchase_invoice_border || 'none',
    });
  };

  const resetToDefaults = () => {
    setForm({ ...DEFAULTS });
    brand?.apply({
      primary: DEFAULTS.brand_primary,
      accent: DEFAULTS.brand_accent,
      success: DEFAULTS.brand_success,
      warning: DEFAULTS.brand_warning,
      danger: DEFAULTS.brand_danger,
      invoice_border: 'none',
      estimate_border: 'none',
      sales_order_border: 'none',
      purchase_order_border: 'none',
      purchase_invoice_border: 'none',
    });
    toast.success('Reset to default theme');
  };

  const applyPreset = (preset) => {
    const updated = { ...form, brand_primary: preset.primary, brand_accent: preset.accent };
    setForm(updated);
    brand?.apply({
      primary: preset.primary,
      accent: preset.accent,
      success: form.brand_success,
      warning: form.brand_warning,
      danger: form.brand_danger,
      invoice_border: form.invoice_border || 'none',
      estimate_border: form.estimate_border || 'none',
      sales_order_border: form.sales_order_border || 'none',
      purchase_order_border: form.purchase_order_border || 'none',
      purchase_invoice_border: form.purchase_invoice_border || 'none',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...company, ...form };
      // Clear null-color fields to empty string (not null)
      ['invoice_color','estimate_color','sales_order_color','purchase_order_color','sales_invoice_color','purchase_invoice_color'].forEach(k => {
        if (!payload[k]) payload[k] = null;
      });
      await companyAPI.update(payload);
      const branding = {
        primary: form.brand_primary,
        accent: form.brand_accent,
        success: form.brand_success,
        warning: form.brand_warning,
        danger: form.brand_danger,
        invoice_color: form.invoice_color || null,
        estimate_color: form.estimate_color || null,
        sales_order_color: form.sales_order_color || null,
        purchase_order_color: form.purchase_order_color || null,
        sales_invoice_color: form.sales_invoice_color || null,
        purchase_invoice_color: form.purchase_invoice_color || null,
        invoice_border: form.invoice_border || 'none',
        estimate_border: form.estimate_border || 'none',
        sales_order_border: form.sales_order_border || 'none',
        purchase_order_border: form.purchase_order_border || 'none',
        purchase_invoice_border: form.purchase_invoice_border || 'none',
      };
      localStorage.setItem('ledgerpro_branding', JSON.stringify(branding));
      brand?.apply(branding);
      toast.success('Theme & branding saved');
    } catch (err) { toast.error(err.message); }
    setSaving(false);
  };

  if (loading) return <div className="spinner" style={{ margin: '40px auto' }} />;

  return (
    <div>
      <div className="toolbar">
        <h2><Palette size={20} style={{ verticalAlign: '-3px', marginRight: 8 }} />Theme & Branding</h2>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Main Palette */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header"><h3>Brand Palette</h3></div>
          <div className="card-body">
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
              Set your company's primary brand colors. These apply to the sidebar, toolbar, buttons, workflow panels, and document headers.
            </p>
            <div className="form-grid">
              <ColorField label="Primary (Sidebar / Title Bar)" value={form.brand_primary} onChange={set('brand_primary')}
                description="Deep navy — used for the sidebar and title bar background." />
              <ColorField label="Accent (Buttons / Active States)" value={form.brand_accent} onChange={set('brand_accent')}
                description="Professional blue — used for buttons, active tabs, and links." />
              <ColorField label="Success (Paid / Completed)" value={form.brand_success} onChange={set('brand_success')}
                description="Green — used for paid badges, success states, and positive indicators." />
              <ColorField label="Warning (Partial / Pending)" value={form.brand_warning} onChange={set('brand_warning')}
                description="Amber — used for partial payment badges and warning states." />
              <ColorField label="Danger (Overdue / Voided)" value={form.brand_danger} onChange={set('brand_danger')}
                description="Red — used for overdue badges, voided states, and error messages." />
            </div>

            {/* Presets */}
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Quick Presets</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {PRESETS.map(p => (
                  <button key={p.name} type="button" onClick={() => applyPreset(p)}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', border: '1px solid var(--border)',
                      borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 500 }}>
                    <span style={{ width: 14, height: 14, borderRadius: 3, background: p.primary, display: 'inline-block' }} />
                    <span style={{ width: 14, height: 14, borderRadius: 3, background: p.accent, display: 'inline-block' }} />
                    {p.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Document Branding */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header"><h3>Document Branding Colors</h3></div>
          <div className="card-body">
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
              Override the accent color for specific document types. Leave blank to use the main accent color.
            </p>
            <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
              <DocColorField label="Sales Invoices" value={form.sales_invoice_color || form.invoice_color || ''} onChange={set('invoice_color')}
                description="Accent color for invoice headers and totals." docType="invoice" accentFallback={form.brand_accent} />
              <DocColorField label="Estimates" value={form.estimate_color || ''} onChange={set('estimate_color')}
                description="Accent color for estimate documents." docType="estimate" accentFallback={form.brand_accent} />
              <DocColorField label="Sales Orders" value={form.sales_order_color || ''} onChange={set('sales_order_color')}
                description="Accent color for sales order documents." docType="sales_order" accentFallback={form.brand_accent} />
              <DocColorField label="Purchase Orders" value={form.purchase_order_color || ''} onChange={set('purchase_order_color')}
                description="Accent color for purchase order documents." docType="purchase_order" accentFallback={form.brand_accent} />
              <DocColorField label="Bills / Purchase Invoices" value={form.purchase_invoice_color || ''} onChange={set('purchase_invoice_color')}
                description="Accent color for bill documents." docType="purchase_invoice" accentFallback={form.brand_accent} />
            </div>

            {/* Match Brand & Clear buttons */}
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)', display: 'flex', gap: 8, alignItems: 'center' }}>
              <button type="button" className="btn btn-outline" onClick={() => {
                setForm(f => ({
                  ...f,
                  invoice_color: f.brand_accent,
                  estimate_color: f.brand_accent,
                  sales_order_color: f.brand_accent,
                  purchase_order_color: f.brand_accent,
                  purchase_invoice_color: f.brand_accent,
                  sales_invoice_color: f.brand_accent,
                }));
              }} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                <span style={{ width: 14, height: 14, borderRadius: 3, background: form.brand_accent, display: 'inline-block', border: '1px solid var(--border)' }} />
                Match Brand Accent
              </button>
              <button type="button" className="btn btn-outline" onClick={() => {
                setForm(f => ({
                  ...f,
                  invoice_color: '',
                  estimate_color: '',
                  sales_order_color: '',
                  purchase_order_color: '',
                  purchase_invoice_color: '',
                  sales_invoice_color: '',
                }));
              }} style={{ fontSize: 12 }}>
                Clear All (Use Accent Default)
              </button>
              <span style={{ fontSize: 11, color: 'var(--text-secondary)', marginLeft: 4 }}>
                Current accent: <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 2, background: form.brand_accent, verticalAlign: '-2px', border: '1px solid var(--border)' }} /> {form.brand_accent}
              </span>
            </div>

            {/* Header Border Styles */}
            <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Header Border Styles</div>
              <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 12 }}>
                Choose the border style below the document header (title row) for each document type.
              </p>
              <div className="form-grid">
                <BorderStyleField label="Sales Invoices" value={form.invoice_border} onChange={set('invoice_border')} accentColor={form.invoice_color || form.brand_accent} />
                <BorderStyleField label="Estimates" value={form.estimate_border} onChange={set('estimate_border')} accentColor={form.estimate_color || form.brand_accent} />
                <BorderStyleField label="Sales Orders" value={form.sales_order_border} onChange={set('sales_order_border')} accentColor={form.sales_order_color || form.brand_accent} />
                <BorderStyleField label="Purchase Orders" value={form.purchase_order_border} onChange={set('purchase_order_border')} accentColor={form.purchase_order_color || form.brand_accent} />
                <BorderStyleField label="Bills / Purchase Invoices" value={form.purchase_invoice_border} onChange={set('purchase_invoice_border')} accentColor={form.purchase_invoice_color || form.brand_accent} />
              </div>
            </div>
          </div>
        </div>

        {/* Live Preview */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header"><h3><Eye size={16} style={{ verticalAlign: '-2px', marginRight: 6 }} />Live Preview</h3></div>
          <div className="card-body">
            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              {/* Mini sidebar preview */}
              <div style={{ width: 180, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)', flexShrink: 0 }}>
                <div style={{ background: form.brand_primary, padding: '10px 12px', color: '#fff', fontSize: 12, fontWeight: 700, letterSpacing: 0.5 }}>
                  LedgerPro
                </div>
                <div style={{ background: form.brand_accent, color: '#fff', padding: '6px 12px', fontSize: 11, fontWeight: 600 }}>
                  Dashboard
                </div>
                <div style={{ background: form.brand_primary, padding: '6px 12px', color: '#cbd5e1', fontSize: 11 }}>
                  Invoices
                </div>
                <div style={{ background: form.brand_primary, padding: '6px 12px', color: '#cbd5e1', fontSize: 11 }}>
                  Reports
                </div>
              </div>

              {/* Button preview */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>Buttons & Badges</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ background: form.brand_accent, color: '#fff', padding: '5px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600 }}>Save</span>
                  <span style={{ background: form.brand_success, color: '#fff', padding: '5px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600 }}>Paid</span>
                  <span style={{ background: form.brand_warning, color: '#fff', padding: '5px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600 }}>Partial</span>
                  <span style={{ background: form.brand_danger, color: '#fff', padding: '5px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600 }}>Overdue</span>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
                  <span style={{ border: `1px solid ${form.brand_accent}`, color: form.brand_accent, padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>Draft</span>
                  <span style={{ border: `1px solid ${form.brand_accent}`, color: form.brand_accent, padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>Submitted</span>
                  <span style={{ background: `${form.brand_success}18`, color: form.brand_success, padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>Completed</span>
                </div>
              </div>

              {/* Workflow item preview */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>Workflow Accent</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 12px', textAlign: 'center', fontSize: 11, fontWeight: 600, background: '#fff', position: 'relative', overflow: 'hidden' }}>
                    <div style={{ position: 'absolute', bottom: 0, left: '20%', right: '20%', height: 2, background: form.brand_accent, borderRadius: 2 }} />
                    Invoice
                  </div>
                  <div style={{ background: `linear-gradient(135deg, ${form.brand_accent}, ${form.brand_primary})`, borderRadius: 8, padding: '8px 12px', textAlign: 'center', fontSize: 11, fontWeight: 600, color: '#fff' }}>
                    Create Invoice
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="form-actions" style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-outline" onClick={resetToDefaults}>
            <RotateCcw size={14} /> Reset to Default
          </button>
          <button type="button" className="btn btn-outline" onClick={applyPreview}>
            <Eye size={14} /> Preview
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            <Save size={14} /> {saving ? 'Saving...' : 'Save Branding'}
          </button>
        </div>
      </form>
    </div>
  );
}
